<template>
  <gg-modal :is-active="isActive" @close="close">
    <template v-slot:toolbar>
      Create Forwarder
    </template>
    <template v-slot:body>
      <v-form class="pt-2" @submit.prevent="create">
        <v-row>
          <v-col cols="12" sm="6" class="py-0">
            <gg-text-field
              outlined
              dense
              label="Address to Forward"
              placeholder="ex. info"
              autocomplete="off"
              v-model="model.address"
              v-validate="'required'"
              name="Address"
              :error-messages="errors.collect('Address')"
            />
          </v-col>
          <v-col cols="12" sm="6" class="py-0">
            <gg-select
              v-model="model.domain"
              label="Domain"
              :items="domains"
              :loading="loading"
              :disabled="loading"
              v-validate="'required'"
              data-vv-name="domain"
              data-vv-as="Domain"
              :error-messages="errors.collect('domain')"
            />
          </v-col>
          <v-col cols="12" class="py-0">
            <gg-text-field
              outlined
              dense
              label="Forward To"
              placeholder="ex. someone@example.com"
              autocomplete="off"
              v-model="model.destination"
              v-validate="'required|email'"
              name="Destination"
              :error-messages="errors.collect('Destination')"
            />
          </v-col>
          <v-col v-if="address && model.destination" cols="12" class="pt-0">
            <p class="body-2 mb-0">
              Mail sent to <strong>{{ address }}</strong> will be forwarded to
              <strong>{{ model.destination }}</strong>.
            </p>
          </v-col>
        </v-row>
      </v-form>
    </template>
    <template v-slot:actions>
      <v-spacer></v-spacer>
      <gg-btn secondary @click="close">Cancel</gg-btn>
      <gg-btn primary :loading="saving" :disabled="loading" @click="create">Create Forwarder</gg-btn>
    </template>
  </gg-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

/**
 * A forwarder for any address on the account, opened from the Forwarders
 * empty state (AM-1107). Unlike CreateForwarderModal, which forwards one
 * mailbox, the address here is typed and need not be a mailbox.
 */
export default {
  name: 'CreateAccountForwarderModal',
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    service: {
      type: Object,
      required: true,
    },
    // The domain the account is opened on: the main domain or an addon.
    scopeDomain: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      model: {
        address: '',
        domain: '',
        destination: '',
      },
      loading: false,
      saving: false,
    }
  },
  computed: {
    ...mapGetters('emailHub', ['scopedDomains']),
    domains () {
      const domains = this.scopedDomains(this.service.id, this.scopeDomain)
      return domains.length ? domains : [this.scopeDomain]
    },
    address () {
      if (!this.model.address || !this.model.domain) return ''
      return `${this.model.address}@${this.model.domain}`
    },
  },
  methods: {
    ...mapActions('emailHub', ['loadDomainsForService', 'addMailboxForwarder']),
    ...mapActions(['reportError', 'reportSuccess']),
    close () {
      this.$emit('close')
    },
    async open () {
      this.model = { address: '', domain: this.scopeDomain, destination: '' }
      this.$validator.reset()

      this.loading = true
      try {
        await this.loadDomainsForService(this.service.id)
      } catch (e) {
        // The opened domain is still offered.
        console.warn('CreateAccountForwarderModal: unable to load domains', e)
      } finally {
        this.loading = false
      }
    },
    async create () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      this.saving = true
      try {
        await this.addMailboxForwarder({
          serviceId: this.service.id,
          email: this.address,
          domain: this.model.domain,
          destination: this.model.destination.trim(),
        })
      } catch (e) {
        this.saving = false
        this.reportError(e)
        return
      }

      this.saving = false
      this.reportSuccess('Forwarder successfully created.')
      this.$emit('created')
      this.close()
    },
  },
  watch: {
    isActive (active) {
      if (active) this.open()
    },
  },
  created () {
    if (this.isActive) this.open()
  },
}
</script>
